/**
 * Site QA mute protocol.
 *
 * The runner may:
 *   1. Check off a box that Grok authored AND the owner approved, after a passing run.
 *   2. On fail: save a PNG that shows the error, then open a Grok window whose
 *      prompt is that screenshot path plus a short job brief (which box, what
 *      the actions expected, Playwright error). Inbox UI still lists the path.
 *
 * The runner may not:
 *   add boxes, rewrite titles, check off unapproved / owner-authored boxes,
 *   or dump logs / extra files instead of the screenshot.
 */

import type { QaItem } from "./types";

export type CheckOffResult =
  | { ok: true; item: QaItem }
  | { ok: false; reason: string };

export function canCheckOff(item: QaItem): CheckOffResult {
  if (item.authoredBy !== "grok") {
    return { ok: false, reason: "only Grok-authored boxes may be checked off" };
  }
  if (!item.approved) {
    return { ok: false, reason: "owner has not approved this box" };
  }
  return { ok: true, item };
}

export function checkOff(item: QaItem, at = new Date().toISOString()): CheckOffResult {
  const gate = canCheckOff(item);
  if (!gate.ok) return gate;
  return {
    ok: true,
    item: {
      ...item,
      status: "pass",
      passAt: at,
    },
  };
}

/** The only payload the runner may send to a Grok window. */
export function failPost(screenshotPath: string): string {
  return screenshotPath.trim();
}

export function approvedGrokItems(items: QaItem[]): QaItem[] {
  return items.filter((i) => i.authoredBy === "grok" && i.approved);
}

export function checklistComplete(items: QaItem[]): boolean {
  const boxes = approvedGrokItems(items);
  return boxes.length > 0 && boxes.every((i) => i.status === "pass");
}
